import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Award, Plus, X, Lightbulb, FileText, Save, Loader2, ClipboardCheck } from "lucide-react";

export function PilotCierreDialog({
  open, onOpenChange, pilot, form, setForm,
  newKpi, setNewKpi, newLeccion, setNewLeccion,
  onSave, onExport, loading,
}) {
  const addKpi = () => {
    if (!newKpi.nombre) return;
    setForm({ ...form, kpis_resultados: [...form.kpis_resultados, newKpi] });
    setNewKpi({ nombre: "", valor: "" });
  };

  const addLeccion = () => {
    if (!newLeccion.trim()) return;
    setForm({ ...form, lecciones_aprendidas: [...form.lecciones_aprendidas, newLeccion.trim()] });
    setNewLeccion("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />Ficha de Cierre — {pilot?.name}
          </DialogTitle>
          <DialogDescription>
            Documenta los resultados del piloto. Guarda primero y luego exporta en PDF.
          </DialogDescription>
        </DialogHeader>

        {loading && !form.resumen_ejecutivo ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="resumen">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="resumen">Resumen</TabsTrigger>
              <TabsTrigger value="propuesta" data-testid="cierre-tab-propuesta">Propuesta</TabsTrigger>
              <TabsTrigger value="kpis">KPIs</TabsTrigger>
              <TabsTrigger value="lecciones">Lecciones</TabsTrigger>
            </TabsList>

            <TabsContent value="resumen" className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Fecha de cierre</Label>
                <Input
                  type="date"
                  value={form.closure_date}
                  onChange={e => setForm({ ...form, closure_date: e.target.value })}
                  data-testid="cierre-closure-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Resumen ejecutivo</Label>
                <Textarea
                  rows={4}
                  placeholder="Describe en 2-3 párrafos el resultado del piloto, qué se validó y el contexto..."
                  value={form.resumen_ejecutivo}
                  onChange={e => setForm({ ...form, resumen_ejecutivo: e.target.value })}
                  data-testid="cierre-resumen-ejecutivo"
                />
              </div>
              <div className="space-y-2">
                <Label>Objetivos alcanzados</Label>
                <Textarea
                  rows={3}
                  placeholder="¿Qué hipótesis se validó? ¿Cuáles fueron los principales logros?"
                  value={form.objetivos_alcanzados}
                  onChange={e => setForm({ ...form, objetivos_alcanzados: e.target.value })}
                  data-testid="cierre-objetivos"
                />
              </div>
              <div className="space-y-2">
                <Label>Próximos pasos</Label>
                <Textarea
                  rows={2}
                  placeholder="¿Qué sigue? Escalado, nueva iteración, cancelación..."
                  value={form.proximos_pasos}
                  onChange={e => setForm({ ...form, proximos_pasos: e.target.value })}
                  data-testid="cierre-proximos-pasos"
                />
              </div>
              <div className="space-y-2">
                <Label>Recomendaciones</Label>
                <Textarea
                  rows={2}
                  placeholder="Recomendaciones para el equipo o para futuros pilotos similares..."
                  value={form.recomendaciones}
                  onChange={e => setForm({ ...form, recomendaciones: e.target.value })}
                  data-testid="cierre-recomendaciones"
                />
              </div>
            </TabsContent>

            <TabsContent value="propuesta" className="space-y-4 pt-2">
              <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-start gap-2 mb-3">
                  <ClipboardCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">Propuesta presentada al sponsor / dirección</div>
                    <p className="text-xs text-muted-foreground">Registro trazable de la entrega formal de la propuesta al cierre del piloto.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox" id="cierre-propuesta-check"
                    checked={!!form.propuesta_presentada}
                    onChange={e => setForm({ ...form, propuesta_presentada: e.target.checked })}
                    data-testid="cierre-propuesta-check"
                  />
                  <Label htmlFor="cierre-propuesta-check" className="cursor-pointer">Sí, la propuesta se entregó</Label>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Fecha de presentación</Label>
                    <Input
                      type="date"
                      value={form.propuesta_fecha || ""}
                      onChange={e => setForm({ ...form, propuesta_fecha: e.target.value })}
                      data-testid="cierre-propuesta-fecha"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Enlace al documento</Label>
                    <Input
                      value={form.propuesta_doc_url || ""}
                      onChange={e => setForm({ ...form, propuesta_doc_url: e.target.value })}
                      placeholder="https://sharepoint.../propuesta.pdf"
                      data-testid="cierre-propuesta-doc-url"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="kpis" className="space-y-4 pt-2">
              <Label>KPIs y Resultados Clave</Label>
              {form.kpis_resultados.map((kpi, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                  <span className="text-sm font-medium flex-1">{kpi.nombre}</span>
                  <span className="text-sm text-primary font-bold">{kpi.valor}</span>
                  <Button
                    size="icon" variant="ghost" className="h-6 w-6"
                    onClick={() => setForm({
                      ...form,
                      kpis_resultados: form.kpis_resultados.filter((_, j) => j !== i),
                    })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="KPI (ej: Reducción averías)"
                  value={newKpi.nombre}
                  onChange={e => setNewKpi({ ...newKpi, nombre: e.target.value })}
                  data-testid="cierre-kpi-nombre"
                />
                <Input
                  placeholder="Valor (ej: 42%)"
                  value={newKpi.valor}
                  onChange={e => setNewKpi({ ...newKpi, valor: e.target.value })}
                  className="w-32"
                  data-testid="cierre-kpi-valor"
                />
                <Button variant="outline" data-testid="cierre-kpi-add" onClick={addKpi}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="lecciones" className="space-y-4 pt-2">
              <Label>Lecciones Aprendidas</Label>
              {form.lecciones_aprendidas.map((l, i) => (
                <div key={i} className="flex items-start gap-2 p-3 border rounded-md bg-amber-50 dark:bg-amber-900/10 border-amber-200">
                  <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm flex-1">{l}</span>
                  <Button
                    size="icon" variant="ghost" className="h-6 w-6"
                    onClick={() => setForm({
                      ...form,
                      lecciones_aprendidas: form.lecciones_aprendidas.filter((_, j) => j !== i),
                    })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  placeholder="Describe una lección aprendida del piloto..."
                  value={newLeccion}
                  onChange={e => setNewLeccion(e.target.value)}
                  data-testid="cierre-leccion-input"
                />
                <Button variant="outline" className="self-end" data-testid="cierre-leccion-add" onClick={addLeccion}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 mr-auto">
            <Button
              variant="outline" size="sm"
              onClick={onExport}
              disabled={loading}
              data-testid="cierre-export-pdf"
            >
              <FileText className="w-4 h-4 mr-1 text-red-500" />PDF
            </Button>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />Cerrar
          </Button>
          <Button onClick={onSave} disabled={loading} data-testid="cierre-save-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar ficha de cierre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
